import TextButton from 'LuxKit/Button/TextButton'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import Heading from 'LuxKit/Typography/Heading'
import { LUX_BUNDLE_LABEL } from 'constants/productLabel'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { nonNullable } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import SearchTile from '../SearchTile'
import HotelBundleSearchTileFeatures from './HotelBundleSearchTileFeatures'
import HotelBundleSearchTileHighlightBanner from './HotelBundleSearchTileHighlightBanner'
import HotelBundleSearchTileMeta from './HotelBundleSearchTileMeta'
import HotelBundleSearchTileOverlineLocations from './HotelBundleSearchTileOverlineLocations'
import HotelBundleSearchTilePricePoint from './HotelBundleSearchTilePricePoint'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import OfferDescriptionContainer from 'components/OfferList/OfferListTiles/OfferDescriptionContainer'
interface Props {
  offer: App.BundleOffer
  filters?: App.OfferListFilters
  showDescription?: boolean
  position?: number
}

function HotelBundleSearchTile({
  offer,
  filters,
  showDescription = false,
  position,
}: Props) {
  const intl = useIntl()
  const offersLocations = useMemo<Array<Array<string>>>(() => {
    return Object.values(offer.bundledOffers).map((offer) => nonNullable([offer.locationHeading, offer.locationSubheading]))
  }, [offer.bundledOffers])

  const title = useMemo(() => {
    if (offer.location) return offer.location
    const offers = Object.values(offer.bundledOffers)
    return offers.map((offer) => offer.property.name).join(' + ')
  }, [offer])

  const showHighlight = !!(filters?.propertyId && !filters?.searchNearby && offer.property?.id === filters.propertyId)

  const offerMetaData = useOfferMetaData(offer.id, filters) as App.BundleOfferListMetaData

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)

  const hidePrice = !!offerMetaData?.hidePricing

  const available = shouldUseRealSearchPrices ? offerMetaData.available : true

  return <SearchTile
    state="highlighted"
    action={<TextButton nonInteractable kind="primary">{intl.formatMessage({
      id: 'FVUp-2wYz0WbVAJXELva',
      defaultMessage: 'View offer',
      description: 'CTA button on hotel bundle search tile to view the offer',
    })}
    </TextButton>}
    banner={<>
      {showDescription && filters && (
        <OfferDescriptionContainer
          filters={filters}
          offerId={offer.id}
          position={position}
        />
      )}
      {showHighlight && <HotelBundleSearchTileHighlightBanner offer={offer} filters={filters} offerMetaData={offerMetaData} />}
    </>}
    features={<HotelBundleSearchTileFeatures offer={offer} />}
    images={offer.images}
    meta={<HotelBundleSearchTileMeta offer={offer} />}
    overline={<HotelBundleSearchTileOverlineLocations offersLocations={offersLocations} />}
    pricePoint={available &&
      <HotelBundleSearchTilePricePoint offer={offer} filters={filters} offerMetaData={offerMetaData} hidePrice={hidePrice} />}
    productLabel={<ProductTypeLabel productType={offer.productType} />}
    title={<><Heading variant="heading5" colour="highlight-secondary" as="span">
      {intl.formatMessage(
        {
          id: 'FOo36ASMi-BHpijpTELO',
          defaultMessage: '{label}:',
          description: 'Lux bundle label with colon prefix for hotel bundle search tile title',
        },
        { label: LUX_BUNDLE_LABEL },
      )}
    </Heading> {title}
    </>}
    offer={offer}
  />
}

export default HotelBundleSearchTile
